import React, { useState } from "react";
import { api, fmtINR, fmtDate, toPaise, PAYMENT_LABELS } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData } from "@/hooks/useAsyncData";
import { Money, StatusBadge, EmptyState, LoadErrorState, LoadingState, MoneyInput } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Archive, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;
const initialFilters = () => ({
  q: "", store_id: "all", payment_type: "all", date_from: "", date_to: "",
  min_amount: "", max_amount: "", include_void: false, only_mine: false,
});

export default function BillsArchivePage() {
  const { user, allowedStores, today, storeById, billVisibility } = useApp();
  const isCashier = user.role === "cashier";
  const visibleFrom = user.role === "admin" || user.role === "manager" ? undefined : billVisibility?.visible_from;
  const [form, setForm] = useState(initialFilters);
  const [committed, setCommitted] = useState(initialFilters);
  const [page, setPage] = useState(0);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const runSearch = () => { setPage(0); setCommitted({ ...form }); };
  const reset = () => { const f = initialFilters(); setForm(f); setPage(0); setCommitted(f); };

  const q = useAsyncData(
    () => {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE, include_void: committed.include_void };
      if (committed.q.trim()) params.q = committed.q.trim();
      if (committed.store_id !== "all") params.store_id = committed.store_id;
      if (committed.payment_type !== "all") params.payment_type = committed.payment_type;
      if (committed.date_from) params.date_from = committed.date_from;
      if (committed.date_to) params.date_to = committed.date_to;
      if (committed.min_amount) params.min_amount_paise = toPaise(committed.min_amount);
      if (committed.max_amount) params.max_amount_paise = toPaise(committed.max_amount);
      if (isCashier && committed.only_mine) params.cashier_id = user.id;
      return api.get("/bills/search", { params }).then((r) => r.data);
    },
    [committed, page]
  );
  const data = q.data;
  const bills = data?.bills || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-xl font-semibold arch-underline">Bills Archive</h1>
        <p className="text-sm text-muted-foreground">
          Search the full bill ledger across dates{isCashier ? " — your whole store" : " and stores"}
          {visibleFrom && <span data-testid="archive-visibility-note"> · bills before {fmtDate(visibleFrom)} are hidden by admin</span>}
        </p>
      </div>

      <Card className="rounded-lg">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5 lg:col-span-2">
              <Label>Bill no / customer / phone / narration</Label>
              <Input value={form.q} onChange={(e) => set({ q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="e.g. M-1024, Sharma or a remark" className="h-10" data-testid="archive-search-input" />
            </div>
            {!isCashier && (
              <div className="space-y-1.5">
                <Label>Store</Label>
                <Select value={form.store_id} onValueChange={(v) => set({ store_id: v })}>
                  <SelectTrigger className="h-10" data-testid="archive-store-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All stores</SelectItem>
                    {allowedStores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Payment mode</Label>
              <Select value={form.payment_type} onValueChange={(v) => set({ payment_type: v })}>
                <SelectTrigger className="h-10" data-testid="archive-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All modes</SelectItem>
                  {Object.entries(PAYMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>From date</Label>
              <Input type="date" value={form.date_from} min={visibleFrom || undefined} max={today} onChange={(e) => set({ date_from: e.target.value })}
                className="h-10" data-testid="archive-date-from" />
            </div>
            <div className="space-y-1.5">
              <Label>To date</Label>
              <Input type="date" value={form.date_to} min={visibleFrom || undefined} max={today} onChange={(e) => set({ date_to: e.target.value })}
                className="h-10" data-testid="archive-date-to" />
            </div>
            <div className="space-y-1.5">
              <Label>Min amount (₹)</Label>
              <MoneyInput value={form.min_amount} onChange={(v) => set({ min_amount: v })} className="h-10" testId="archive-min-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Max amount (₹)</Label>
              <MoneyInput value={form.max_amount} onChange={(v) => set({ max_amount: v })} className="h-10" testId="archive-max-amount" />
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.include_void} onCheckedChange={(v) => set({ include_void: !!v })} data-testid="archive-include-void" />
                Include voided bills
              </label>
              {isCashier && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.only_mine} onCheckedChange={(v) => set({ only_mine: !!v })} data-testid="archive-only-mine" />
                  Only my bills
                </label>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="archive-reset-button">
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset
              </Button>
              <Button size="sm" onClick={runSearch} className="bg-primary hover:bg-[hsl(var(--sapphire-2))]" data-testid="archive-search-button">
                <Search className="h-3.5 w-3.5 mr-1.5" />Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {q.error ? (
        <LoadErrorState error={q.error} onRetry={q.reload} title="Could not search the archive" />
      ) : q.loading ? (
        <Card className="rounded-lg"><CardContent className="p-4"><LoadingState /></CardContent></Card>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2 text-sm" data-testid="archive-results-summary">
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">{total}</span> bill{total === 1 ? "" : "s"} found
              {total > 0 && <> · total <Money paise={data.totals.amount_paise} className="text-foreground font-semibold" /></>}
              {data?.totals?.less_taken_paise > 0 && <> · less taken <Money paise={data.totals.less_taken_paise} className="text-[hsl(var(--warning))]" /></>}
            </p>
            {pages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="archive-prev-page">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs text-muted-foreground font-mono-num">Page {page + 1} / {pages}</span>
                <Button variant="outline" size="sm" className="h-8" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} data-testid="archive-next-page">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>

          <Card className="rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="table-compact">
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead>
                  {!isCashier && <TableHead>Store</TableHead>}
                  <TableHead>Bill No</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Cashier</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Payments</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {bills.length === 0 && (
                    <TableRow><TableCell colSpan={isCashier ? 7 : 8}>
                      <EmptyState icon={Archive} title="No bills match" sub="Adjust the filters and search again" />
                    </TableCell></TableRow>
                  )}
                  {bills.map((b) => (
                    <TableRow key={b.id} className={b.status === "void" ? "opacity-50" : ""} data-testid={`archive-row-${b.bill_no}`}>
                      <TableCell className="font-mono-num text-xs whitespace-nowrap">{fmtDate(b.business_date)}</TableCell>
                      {!isCashier && <TableCell className="text-xs">{storeById(b.store_id)?.name || "—"}</TableCell>}
                      <TableCell className="font-mono-num font-semibold">{b.bill_no}</TableCell>
                      <TableCell className="text-xs">{b.customer_name || <span className="text-muted-foreground">—</span>}
                        {b.customer_phone && <span className="block text-[11px] text-muted-foreground font-mono-num">{b.customer_phone}</span>}
                        {b.narration && <p className="text-[11px] italic text-muted-foreground/90 line-clamp-2 max-w-[260px]" title={b.narration} data-testid={`archive-narration-${b.bill_no}`}>{b.narration}</p>}
                      </TableCell>
                      <TableCell className="text-xs">{b.cashier_name}</TableCell>
                      <TableCell className="amount-cell"><Money paise={b.amount_paise} /></TableCell>
                      <TableCell className="text-xs">
                        {b.payments.map((p, i) => (
                          <span key={i} className="inline-block mr-1.5 whitespace-nowrap">
                            {PAYMENT_LABELS[p.type]}{p.bank_name ? `·${p.bank_name}` : ""} <Money paise={p.amount_paise} className="text-muted-foreground" />
                          </span>
                        ))}
                        {b.less_taken_paise > 0 && <span className="text-[hsl(var(--warning))] text-[11px] whitespace-nowrap">less {fmtINR(b.less_taken_paise)}</span>}
                        {b.excess && (b.excess.return_mode === "keep"
                          ? <span className="text-[hsl(var(--success))] text-[11px] whitespace-nowrap">+{fmtINR(b.excess.amount_paise)} kept</span>
                          : <span className="text-[hsl(var(--danger))] text-[11px] whitespace-nowrap">↩ {fmtINR(b.excess.amount_paise)} {b.excess.return_mode}</span>)}
                      </TableCell>
                      <TableCell><StatusBadge status={b.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
